import React, { Component } from 'react';
import PropTypes from 'prop-types';
import PlyrWithClosedCaptions from '@lwtears/lwt-common-frontend/lib/PlayerWithClosedCaptions';
import InactivityManager from 'utils/InactivityManager';
import styles from './TagBags.module.scss';
import { captionFormat } from 'utils/caption-util';
import { formatPlyrVolume } from 'utils/plyr-util';
import { getLocale } from '@lwtears/lwt-common-frontend/lib/@common/util/i18n-util';
import { withLwtStore } from '@lwtears/lwt-common-frontend/lib/LwtApp';
import { t } from '../translations';

const TIMEOUT = process.env.REACT_APP_INACTIVITY_TIMEOUT;

const REASON = {
  didNotTouchTagBag: 'Did not touch the tag bag'
};

class TagBags extends Component {
  constructor(props) {
    super(props);
    this.state = { clicked: false, player: null };
    this.inactivityManager = new InactivityManager(
      TIMEOUT,
      () => this.playInstructions(),
      REASON.didNotTouchTagBag
    );
  }

  playInstructions() {
    const {
      props: {
        currentRound: {
          data: {
            instructions: { beforeOpen }
          },
          audio
        }
      }
    } = this;

    const snd = audio[beforeOpen.src];
    snd && snd.play();
  }

  componentDidMount() {
    setTimeout(() => {
      this.playInstructions();
      this.inactivityManager.watch();
    }, 500);
  }

  componentWillUnmount() {
    this.inactivityManager.clear();
    this.props.currentRound.stopAllSounds();
  }

  handleEnd = () => {
    const {
      props: {
        currentRound: {
          data: { number, bagOpen },
          audio
        },
        onRoundEnd
      }
    } = this;
    const sndNumber = audio[number.src];
    const sndFx = audio[bagOpen.src];

    sndNumber.once('end', () => {
      onRoundEnd();
    });

    sndFx &&
      sndFx.once('end', () => {
        sndNumber && sndNumber.play();
      });

    sndFx && sndFx.play();
  };

  handleStart = () => {
    const {
      props: {
        currentRound: {
          data: { openingSound },
          audio
        }
      }
    } = this;

    if (openingSound) {
      const snd = audio[openingSound.src];
      snd && snd.play();
    }
  };

  onTagBagClick = () => {
    const { player } = this.state;
    this.inactivityManager.clear();
    this.setState({ clicked: true });
    if (player) player.play();
  };

  handleOnDone = () => {
    this.handleEnd();
    this.props.onRoundEnd();
  };

  render() {
    const {
      props: {
        currentRound: {
          data: { bagAnimation, text, bagClosed }
        }
      },
      state: { clicked }
    } = this;

    const {
      accessibility: {
        closedCaptionsSettings,
        audioSettings,
        videoNarrationSettings: { showSettings }
      }
    } = this.props.lwtStoreState.settings;

    const videoStyles = [styles.videoContainer];
    clicked && videoStyles.push(styles.showing);

    const PlyrProps = {
      src: bagAnimation.src,
      type: 'video/mp4',
      title: '',
      onLoad: this.handleStart,
      onDone: this.handleOnDone,
      options: {
        autoplay: true,
        withNativeControls: true,
        locale: getLocale(),
        narrationsAsDefault: showSettings,
        showSettings: showSettings,
        captions: {
          active: closedCaptionsSettings.showClosedCaptions,
          styles: { fontSize: closedCaptionsSettings.closedCaptionSize },
          hideCaptionToggle: true
        },

        containerStyles: { display: 'flex', maxHeight: 400 },
        ariaLabel: bagAnimation.description
      },
      config: { volume: formatPlyrVolume(audioSettings.volume, audioSettings.muteSounds) },
      captions: [captionFormat(bagAnimation.vtt)],
      narration: {
        src: bagAnimation.narrationSrc ?? '',
        captions: [{ src: bagAnimation.narrationVtt ?? '', label: '', kind: 'captions' }]
      }
    };

    return (
      <div className={styles.TagBag}>
        <div className={styles.number}>
          <span id="tag-bag-number">{text}</span>
        </div>
        <div className={styles.video} id="tag-bag">
          {!clicked && (
            <img
              tabIndex="0"
              role="button"
              aria-label={t('TagBag')}
              src={bagClosed.src}
              alt={`${t('tagBagNumber')} ${text}`}
              onClick={this.onTagBagClick}
              onKeyDown={ev => ev.key === 'Enter' && this.onTagBagClick()}
            />
          )}
          <div className={videoStyles.join(' ')}>
            {clicked && <PlyrWithClosedCaptions {...PlyrProps} />}
          </div>
        </div>
      </div>
    );
  }
}
export default withLwtStore(TagBags);

TagBags.propTypes = {
  currentRound: PropTypes.object,
  onRoundEnd: PropTypes.func,
  lwtStoreState: PropTypes.object
};
